// Action
import { generatePutSignedUrl } from 'api/action/s3';

// Sentry Logging
import * as Sentry from '@sentry/react';

// Dropzone
import { FileWithPath } from '@mantine/dropzone';

// Buffer
import { Buffer } from 'buffer';

// Axios
import axios from 'axios';

interface UploadFileToSignedURLInput {
  signedURL: string;
  data: Blob | ArrayBuffer | Uint8Array;
  contentType: string;
  onProgress?: (percentage: number) => void;
}

export const uploadFileToSignedURL = async ({
  signedURL,
  data,
  contentType,
  onProgress,
}: UploadFileToSignedURLInput): Promise<void> => {
  await axios({
    method: 'PUT',
    url: signedURL,
    data,
    headers: {
      'Content-Type': contentType,
    },
    onUploadProgress: ({ loaded, total }) => {
      if (!total) return;
      onProgress?.(Math.round((loaded / total) * 100));
    },
  });
};

/**
 * @desc Entrance point function exported to start a file upload from the frontend
 * @param {Array<FileWithPath>} files - the file(s) the user has chosen to upload
 * @param {String} bucket - the bucket to upload the files to without any environment suffix (its added in the backend automatically based on env)
 */
export const uploadFileToS3 = async (
  files: Array<FileWithPath>,
  bucket: string,
  filenameOverrides?: string[]
) => {
  const filesToUpload = await fileUploadPrep(files, filenameOverrides);
  const promise = filesToUpload.map(async (file) =>
    uploadFile(file.filename, file.bytes, bucket, file.type)
  );
  return Promise.all(promise);
};

/**
 * @desc Prepares the file for upload. We read the files byte contents and store it. Unfortunately this is called multiple times throughout the app (specifically when we remove from the file list)
 *       this was due to the fact that JSON.Stringify would not work on the uploadFiles if we only called this at the end.
 * @param {Array<FileWithPath>} files - contains a list of files we should read and store in uploadFiles.current
 * @return {Array<{filename: string, bytes: string|ArrayBuffer, type: string}>} uploadData - contains a list of objects preparred to be uploaded to S3
 */
const fileUploadPrep = async (
  files: Array<FileWithPath>,
  filenameOverrides?: string[]
) => {
  return Promise.all(
    files.map(async (fileElement, index) => {
      const filename = filenameOverrides?.[index]
        ? filenameOverrides[index]
        : fileElement.name;
      const fileData = await readFile(fileElement);

      return {
        filename: filename,
        bytes: fileData,
        type: fileElement.type,
      };
    })
  );
};

/**
 * @desc Reads the supplied file from the filesystem and resolves its bytes
 * @param {Object} file - the file we are reading
 * @return {Promise} result -  resolution of file bytes
 */
const readFile = async (file: FileWithPath): Promise<string | ArrayBuffer> => {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.readAsDataURL(file);
    fr.onload = () => {
      resolve(fr.result);
    };
    fr.onerror = reject;
  });
};

/**
 * @desc Uploads video bytes to s3 with proper name
 * @param {String} filekey - The key to upload the file as in S3
 * @param {Bytes} bytes - The bytes of the file to upload
 * @param {String} bucket - The bucket to upload the file to
 * @return {String} fileKey - the modified key that the file was uploaded as (patientID/filekey)
 */
// NOTE: Exported for unit testing granular branches (ArrayBuffer vs data URL)
export const uploadFile = async (
  filekey: string,
  bytes: string | ArrayBuffer,
  bucket: string,
  fileType: string
): Promise<string | null> => {
  try {
    const signedUrl = await generatePutSignedUrl({
      key: filekey,
      bucket: bucket,
      type: fileType,
    });
    // Ensure the data is properly encoded
    let modBytes;
    if (typeof bytes === 'string' && bytes.startsWith('data:')) {
      modBytes = Buffer.from(bytes.split(',')[1], 'base64');
    } else if (
      bytes instanceof ArrayBuffer ||
      Object.prototype.toString.call(bytes) === '[object ArrayBuffer]'
    ) {
      // Support cross-realm ArrayBuffers by using toString tag fallback
      modBytes = Buffer.from(bytes as ArrayBuffer);
    } else {
      throw new Error('Unsupported data type for file upload');
    }
    await uploadFileToSignedURL({
      signedURL: signedUrl,
      data: modBytes,
      contentType: fileType,
    });

    Sentry.setContext('s3_upload', {
      fileKey: filekey,
      bucket: bucket,
    });
    Sentry.captureMessage('File uploaded to S3', {
      level: 'info',
    });
    return filekey;
  } catch (error) {
    Sentry.withScope((scope) => {
      scope.setTag('fileName', 'FileUpload.js');
      Sentry.captureException(error);
    });
    return null;
  }
};
